import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map } from 'rxjs';
import { ApiService } from './api.service';
import { DigiResponse } from '@core/models/response/core';
import { SchoolNotificationRecord } from '@core/models/domain/school-notification.domain';
import { buildApiEndpoint, handleError } from '@core/utils/service';

/**
 * Backend calls for the school notification inbox.
 *
 * The notices themselves belong to the school and are permanent. What each call carries is the
 * caller's own view of them: which they have read, and what they have cleared. The school and the
 * user are both taken from the token by the backend, never sent from here.
 */
@Injectable({ providedIn: 'root' })
export class SchoolNotificationService {
  private readonly API_URI: string;

  constructor(private http: HttpClient, private apiService: ApiService) {
    this.API_URI = this.apiService.getApiURL('UserService', 'school-account');
  }

  /** Everything this user has not cleared, newest first, flagged read or unread for them alone. */
  load(): Observable<SchoolNotificationRecord[]> {
    return this.http
      .get<DigiResponse<SchoolNotificationRecord[]>>(buildApiEndpoint(this.API_URI, 'notifications'))
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== 'OK') {
            throw response?.statusCode ?? new Error('Loading notifications failed');
          }
          return Array.isArray(response.body) ? response.body : [];
        }),
        catchError((error) => handleError<SchoolNotificationRecord[]>('Loading notifications', error))
      );
  }

  markAsRead(notificationIds: string[]): Observable<void> {
    return this.post('notifications/read', { notificationIds }, 'Marking notifications as read');
  }

  markAllAsRead(): Observable<void> {
    return this.post('notifications/read-all', {}, 'Marking notifications as read');
  }

  /** Empties this user's inbox. The records behind the notices, and everyone else's inbox, are untouched. */
  clearAll(): Observable<void> {
    return this.post('notifications/clear', {}, 'Clearing notifications');
  }

  private post(path: string, body: unknown, action: string): Observable<void> {
    return this.http
      .post<DigiResponse<string>>(buildApiEndpoint(this.API_URI, path), body)
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== 'OK') {
            throw response?.statusCode ?? new Error(action + ' failed');
          }
          return undefined;
        }),
        catchError((error) => handleError<void>(action, error))
      );
  }
}
